/**
 * Route → document-title mapping for the authenticated app and admin panel.
 *
 * Titles follow the pattern "<Page> · Polaris" (or "· Polaris Admin"), so the
 * specific page reads first in the browser tab, history, and bookmarks. The
 * two layouts (AppLayout / AdminLayout) resolve the current route to a title
 * with `titleForPath()` and render it via <PageTitle>. Detail pages may render
 * their own <PageTitle> with the entity name to override the generic fallback.
 */

// Ordered most-specific first; the first matching pattern wins.
const ROUTES = [
  // ── App (/app/*) ─────────────────────────────────────────────
  [/^\/app\/?$/, "Dashboard"],
  [/^\/app\/clients\/imports\/?$/, "Import clients"],
  [/^\/app\/clients\/[^/]+$/, "Client"],
  [/^\/app\/clients\/?$/, "Clients"],
  [/^\/app\/trips\/[^/]+$/, "Trip"],
  [/^\/app\/trips\/?$/, "Trips"],
  [/^\/app\/pipeline\/?$/, "Pipeline"],
  [/^\/app\/flights\/?$/, "Flights"],
  [/^\/app\/places\/?$/, "Places"],
  [/^\/app\/ai\/?$/, "AI Assistant"],
  [/^\/app\/messaging\/?$/, "Messaging"],
  [/^\/app\/tasks\/?$/, "Tasks"],
  [/^\/app\/marketing\/campaigns\/?$/, "Campaigns"],
  [/^\/app\/marketing\/segments\/?$/, "Audiences"],
  [/^\/app\/marketing\/popups\/?$/, "Lead forms"],
  [/^\/app\/marketing\/leads\/?$/, "Leads"],
  [/^\/app\/marketing\/calls\/?$/, "Calls"],
  [/^\/app\/marketing\/social\/?$/, "Social"],
  [/^\/app\/marketing\/?$/, "Marketing"],
  [/^\/app\/settings\/?$/, "Settings"],
  [/^\/app\/agency-admin\/?$/, "Agency admin"],

  // ── Admin (/admin/*) ─────────────────────────────────────────
  [/^\/admin\/?$/, "Admin dashboard"],
  [/^\/admin\/agencies\/[^/]+$/, "Agency"],
  [/^\/admin\/agencies\/?$/, "Agencies"],
  [/^\/admin\/users\/[^/]+$/, "User"],
  [/^\/admin\/users\/?$/, "Users"],
  [/^\/admin\/promos\/?$/, "Promos"],
  [/^\/admin\/api-keys\/?$/, "API keys"],
  [/^\/admin\/inquiries(?:\/[^/]+)?\/?$/, "Inquiries"],
  [/^\/admin\/blog\/new\/?$/, "New post"],
  [/^\/admin\/blog\/[^/]+$/, "Edit post"],
  [/^\/admin\/blog\/?$/, "Blog"],
];

const DEFAULT_TITLE = "Polaris — AI Travel CRM for Craft Advisors";

/** Resolve a pathname to its page-name segment (null if unknown). */
export function titleForPath(pathname) {
  for (const [re, title] of ROUTES) {
    if (re.test(pathname)) return title;
  }
  return null;
}

/** Compose the full <title> string. */
export function formatTitle(segment, { admin = false } = {}) {
  if (!segment) return DEFAULT_TITLE;
  return `${segment} · ${admin ? "Polaris Admin" : "Polaris"}`;
}
